import { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Switch } from '@/shared/ui/switch';
import { Table, TableBody, TableCell, TableRow } from '@/shared/ui/table';
import { useUpdateAiUser } from '@/features/ai-credits/api/use-ai-admin';
import type { AiUserRow } from '@/features/ai-credits/model/types';
import type { PassbookFlagKey, PassbookPermissionRow } from '../api/use-permissions';
import { userPageFlagGroups, type PageFlag } from '../model/page-flags';
import { FlagLabel } from './flag-table';

/**
 * Páginas de um usuário. O switch mostra o que ele vê de fato; mudar para o
 * contrário do passbook cria uma escolha própria ("própria"), voltar ao igual
 * do passbook apaga a escolha. Sem três estados para explicar.
 */
export function UserPageFlags({
  passbook,
  user,
  onSaved,
}: {
  passbook: PassbookPermissionRow;
  user: AiUserRow;
  onSaved: (row: AiUserRow) => void;
}) {
  const update = useUpdateAiUser(passbook.id);
  // Cópia local para o switch responder na hora; volta ao salvo se o legado recusar.
  const [overrides, setOverrides] = useState<AiUserRow['pageFlags']>(user.pageFlags ?? {});
  useEffect(() => setOverrides(user.pageFlags ?? {}), [user.pageFlags]);
  const customKeys = Object.keys(overrides) as PassbookFlagKey[];

  const baseValue = (flag: PageFlag) => (flag.userOnly ? flag.defaultOn === true : passbook.flags[flag.key]);

  const toggle = (flag: PageFlag, next: boolean) => {
    // Igual ao passbook = sem escolha própria; diferente = escolha própria.
    const value = next === baseValue(flag) ? null : next;
    setOverrides((prev) => {
      const copy = { ...prev };
      if (value === null) delete copy[flag.key];
      else copy[flag.key] = value;
      return copy;
    });
    update.mutate(
      { id: user.id, pageFlags: { [flag.key]: value } },
      {
        onSuccess: (row) => onSaved(row),
        onError: () => {
          setOverrides(user.pageFlags ?? {});
          toast.error(`Não deu para alterar ${flag.label}.`);
        },
      },
    );
  };

  const resetAll = () => {
    const pageFlags = Object.fromEntries(customKeys.map((key) => [key, null]));
    setOverrides({});
    update.mutate(
      { id: user.id, pageFlags },
      {
        onSuccess: (row) => {
          onSaved(row);
          toast.success('Voltou a seguir o passbook.');
        },
        onError: () => {
          setOverrides(user.pageFlags ?? {});
          toast.error('Não deu para desfazer.');
        },
      },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex min-h-8 items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">
          {customKeys.length === 0
            ? 'Segue o passbook.'
            : `${customKeys.length} ${customKeys.length === 1 ? 'página própria' : 'páginas próprias'}`}
        </span>
        {customKeys.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={resetAll} loading={update.isPending}>
            <RotateCcw />
            Voltar ao passbook
          </Button>
        ) : null}
      </div>
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
        <Table>
          <TableBody>
            {userPageFlagGroups().map((group) => (
              <GroupRows key={group.section} title={group.section}>
                {group.flags.map((flag) => {
                  const override = overrides[flag.key];
                  const effective = override ?? baseValue(flag);
                  const id = `user-${user.id}-${flag.key}`;
                  return (
                    <TableRow key={flag.key}>
                      <TableCell className="py-2">
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                          <FlagLabel id={id} label={flag.label} effect={flag.effect} />
                          {override !== undefined ? (
                            <Badge variant="accent" className="font-normal">
                              própria
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="w-14 py-2 text-right">
                        <Switch id={id} checked={effective} onCheckedChange={(next) => toggle(flag, next)} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </GroupRows>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function GroupRows({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <TableRow className="hover:bg-transparent">
        <TableCell colSpan={2} className="bg-muted/40 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </TableCell>
      </TableRow>
      {children}
    </>
  );
}
